import type {
  TaskFrame,
  TaskProposal,
} from "@labeling-tools/api/field-rep/generated/types.gen";
import { MAX_PRODUCT_PROPOSALS } from "@/config/constants";

export const aggregateFrameProposals = (
  frames: TaskFrame[],
  endFrame: number,
): { proposals: TaskProposal[]; clearestFramePosition: number | null } => {
  const validFrames = frames.slice(0, endFrame);

  const bestByProductId = new Map<
    string,
    { proposal: TaskProposal; frameIndex: number }
  >();

  for (let i = 0; i < validFrames.length; i++) {
    for (const proposal of validFrames[i].product_proposals) {
      const existing = bestByProductId.get(proposal.product_id);
      if (!existing || proposal.final_score > existing.proposal.final_score) {
        bestByProductId.set(proposal.product_id, { proposal, frameIndex: i });
      }
    }
  }

  const entries = Array.from(bestByProductId.values());
  entries.sort((a, b) => b.proposal.final_score - a.proposal.final_score);

  const proposals = entries
    .slice(0, MAX_PRODUCT_PROPOSALS)
    .map((e) => e.proposal);
  const clearestFramePosition =
    entries.length > 0 ? entries[0].frameIndex + 1 : null;

  return { proposals, clearestFramePosition };
};

export const toPercent = (score: number) => `${(score * 100).toFixed(1)}%`;

export const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName.toLowerCase();
  if (tagName === "input" && (target as HTMLInputElement).type === "range") {
    return false;
  }

  return (
    target.isContentEditable ||
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select"
  );
};

export const clampFramePosition = (
  value: number,
  totalFrames: number,
): number => {
  if (totalFrames <= 0) {
    return 1;
  }

  return Math.min(Math.max(value, 1), totalFrames);
};

export const getFrameProgressPercentage = (
  framePosition: number,
  totalFrames: number,
): number => {
  if (totalFrames <= 1) {
    return totalFrames === 1 ? 100 : 0;
  }

  return ((framePosition - 1) / (totalFrames - 1)) * 100;
};

export const sanitizeNormalizedFramePoints = (
  points: number[][] | null | undefined,
): [number, number][] => {
  if (!points || points.length === 0) {
    return [];
  }

  const clamp = (value: number) => Math.min(Math.max(value, 0), 1);

  return points.flatMap((point) => {
    const [x, y] = point;

    if (typeof x !== "number" || typeof y !== "number") {
      return [];
    }

    return [[clamp(x), clamp(y)] as [number, number]];
  });
};
